import type { JSX } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Anchor, CalendarClock, Footprints, Plus, Radio, UtensilsCrossed, X } from 'lucide-react'
import type { BehaviourConfig, ServerHook } from '@apiary/shared'
import { api } from '@/lib/api'
import { qk } from '@/lib/query-keys'
import { errorText } from '@/ui/errors'
import { DEFAULT_TOAST_MS, useToastStore } from '@/stores/toasts'
import { Panel } from '@/ui/primitives/Surface'
import { Button } from '@/ui/primitives/Button'
import { Switch } from '@/ui/primitives/Switch'
import { NumberField, TextField } from '@/ui/primitives/Field'
import { Select } from '@/ui/primitives/Select'
import { Chip } from '@/ui/primitives/Chip'
import { HookList, hookProblem } from '@/screens/servers/HookEditor'
import {
  ANTI_AFK_ACTIONS,
  ANTI_AFK_ACTIONS_WITHOUT_PHYSICS,
  ANTI_AFK_ACTION_LABELS,
  KNOWN_BANNED_FOODS,
  MAX_DRIFT_MAX_BLOCKS,
  MAX_DRIFT_MIN_BLOCKS,
  daysLabel,
  hasBehaviourProblems,
  mergeServerUpdate,
  normalizeFoodId,
  parseFoodInput,
  sameConfig,
  splitBannedFoods,
  timezoneOptions,
  toBehavioursPayload,
  toggleAntiAfkAction,
  validateBehaviours,
  withInterval
} from './behaviours-model'
import { ToggleButton } from './behaviours-toggle'
import { ScheduleEditor } from './schedule-editor'

/**
 * Поведения бота: анти-AFK, якорь чанков, автоеда, расписание, хуки сервера.
 *
 * Правки не уезжают на сервер по каждому щелчку тумблера: набор связанный
 * («включил расписание — задай окна»), и промежуточные состояния отправлять
 * бессмысленно. Поэтому копим изменения локально и показываем «Сохранить», пока
 * есть расхождение с сервером; кнопка исчезает, когда его нет.
 *
 * Поведения применяются НА ГОРЯЧУЮ (PATCH /bots/:id, док 06 §5) — бота
 * останавливать не нужно, и об этом сказано в подписи.
 *
 * Валидация до отправки, а не по ответу сервера: почти все ошибки здесь — это
 * настройки, которые API примет, а рантайм молча проигнорирует (окно с нулевой
 * длиной, анти-AFK без движений, расписание без окон). Такой бот выглядит
 * «сломанным без причины», поэтому ошибка показывается рядом с полем сразу.
 */
export interface BehavioursPanelProps {
  botId: string
  value: BehaviourConfig
  canOperate: boolean
}

export function BehavioursPanel({ botId, value, canOperate }: BehavioursPanelProps): JSX.Element {
  const queryClient = useQueryClient()
  const push = useToastStore(state => state.push)
  const [draft, setDraft] = useState<BehaviourConfig>(value)

  // Значение сервера на момент последней синхронизации: нужно, чтобы отличить
  // «пользователь ничего не менял» от «пользователь как раз правит окно».
  const serverRef = useRef<BehaviourConfig>(value)

  useEffect(() => {
    setDraft(current => mergeServerUpdate(current, serverRef.current, value))
    serverRef.current = value
  }, [value])

  const dirty = !sameConfig(draft, value)
  const problems = validateBehaviours(draft)

  /**
   * Реакции проверяются той же функцией, что и в профиле сервера: пустой шаблон
   * компилируется в регулярку, совпадающую со всем подряд, — такой хук сработает
   * на первой же строке чата.
   */
  const hookIssues = draft.hooks
    .map((hook, index) => {
      const problem = hookProblem(hook)
      if (problem === null) return null
      return `${hook.name.trim() === '' ? `реакция ${index + 1}` : hook.name}: ${problem}`
    })
    .filter((item): item is string => item !== null)

  const broken = hasBehaviourProblems(problems) || hookIssues.length > 0

  const save = useMutation({
    mutationFn: (next: BehaviourConfig) =>
      api.bots.update(botId, { behaviours: toBehavioursPayload(next) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.bots.detail(botId) })
      void queryClient.invalidateQueries({ queryKey: qk.bots.lists })
      push({ message: 'Поведения применены', tone: 'success', timeoutMs: DEFAULT_TOAST_MS })
    },
    onError: error =>
      push({ message: errorText(error), tone: 'danger', timeoutMs: DEFAULT_TOAST_MS })
  })

  /**
   * Правка одной секции. Новое значение считается ОТ ПРЕДЫДУЩЕГО состояния, а не
   * от `draft` из замыкания: два изменения в одном тике (клик по нескольким
   * переключателям подряд, автотест, синтетическое событие) иначе перетирают
   * друг друга — доезжает только последнее.
   */
  function patchSection<K extends keyof BehaviourConfig>(
    key: K,
    part: Partial<BehaviourConfig[K]> | ((prev: BehaviourConfig[K]) => Partial<BehaviourConfig[K]>)
  ): void {
    setDraft(current => {
      const prev = current[key]
      const delta = typeof part === 'function' ? part(prev) : part
      return { ...current, [key]: { ...prev, ...delta } }
    })
  }

  const foods = splitBannedFoods(draft.autoEat.bannedFoods)
  const antiAfkLocked = !canOperate || !draft.antiAfk.enabled
  const anchorLocked = !canOperate || !draft.chunkAnchor.enabled
  const autoEatLocked = !canOperate || !draft.autoEat.enabled
  const scheduleLocked = !canOperate || !draft.schedule.enabled

  return (
    <Panel
      title="Поведения"
      note="применяются без перезапуска"
      actions={
        dirty ? (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={save.isPending}
              onClick={() => setDraft(value)}
            >
              Отменить
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={save.isPending}
              disabled={!canOperate || broken}
              title={broken ? 'Сначала исправьте отмеченные поля' : undefined}
              onClick={() => save.mutate(draft)}
            >
              Сохранить
            </Button>
          </div>
        ) : (
          <Chip tone="live" dot>
            всё применено
          </Chip>
        )
      }
      bodyClassName="flex flex-col gap-5"
    >
      <Block
        icon={<Footprints aria-hidden className="size-4" />}
        title="Анти-AFK"
        hint="Периодические микродействия, чтобы сервер не выкинул за простой. Интервал случайный в заданном коридоре — ровный ритм сам выглядит как бот."
      >
        <Switch
          label="Включён"
          checked={draft.antiAfk.enabled}
          disabled={!canOperate}
          onCheckedChange={enabled => patchSection('antiAfk', { enabled })}
        />

        <FieldGroup
          label="Что делает бот"
          error={problems.actions}
          hint={`Выбирается случайное из отмеченного. Если сервер отключил физику, остаются только «${ANTI_AFK_ACTIONS_WITHOUT_PHYSICS.map(action => ANTI_AFK_ACTION_LABELS[action]).join('» и «')}».`}
        >
          {ANTI_AFK_ACTIONS.map(action => (
            <ToggleButton
              key={action}
              pressed={draft.antiAfk.actions.includes(action)}
              disabled={antiAfkLocked}
              onPressedChange={on =>
                patchSection('antiAfk', prev => ({
                  actions: toggleAntiAfkAction(prev.actions, action, on)
                }))
              }
            >
              {ANTI_AFK_ACTION_LABELS[action]}
            </ToggleButton>
          ))}
        </FieldGroup>

        <div className="grid gap-3 sm:grid-cols-3">
          <NumberField
            label="Интервал от, с"
            value={draft.antiAfk.intervalSec[0]}
            min={5}
            max={600}
            error={problems.intervalFrom}
            disabled={antiAfkLocked}
            onChange={event =>
              patchSection('antiAfk', prev => ({
                intervalSec: withInterval(prev.intervalSec, 'from', Number(event.target.value))
              }))
            }
          />
          <NumberField
            label="до, с"
            value={draft.antiAfk.intervalSec[1]}
            min={5}
            max={600}
            error={problems.intervalTo}
            disabled={antiAfkLocked}
            onChange={event =>
              patchSection('antiAfk', prev => ({
                intervalSec: withInterval(prev.intervalSec, 'to', Number(event.target.value))
              }))
            }
          />
          <NumberField
            label="Разброс, %"
            hint="Насколько интервал «плавает» от раза к разу."
            value={draft.antiAfk.jitterPct}
            min={0}
            max={100}
            error={problems.jitter}
            disabled={antiAfkLocked}
            onChange={event => patchSection('antiAfk', { jitterPct: Number(event.target.value) })}
          />
        </div>
      </Block>

      <Block
        icon={<Anchor aria-hidden className="size-4" />}
        title="Якорь чанков"
        hint="Бот держится точки и возвращается, если его отнесло. Точка берётся там, где бот стоит в момент включения."
      >
        <Switch
          label="Включён"
          checked={draft.chunkAnchor.enabled}
          disabled={!canOperate}
          onCheckedChange={enabled => patchSection('chunkAnchor', { enabled })}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <NumberField
            label="Допустимый снос, блоков"
            value={draft.chunkAnchor.maxDriftBlocks}
            min={MAX_DRIFT_MIN_BLOCKS}
            max={MAX_DRIFT_MAX_BLOCKS}
            error={problems.maxDrift}
            disabled={anchorLocked}
            onChange={event =>
              patchSection('chunkAnchor', { maxDriftBlocks: Number(event.target.value) })
            }
          />
          <div className="flex flex-col justify-end gap-2.5">
            <Switch
              label="Идти обратно пешком"
              hint="Выключено — бот вернётся коротким путём, не обходя препятствия."
              checked={draft.chunkAnchor.returnWithPathfinder}
              disabled={anchorLocked}
              onCheckedChange={returnWithPathfinder =>
                patchSection('chunkAnchor', { returnWithPathfinder })
              }
            />
            <Switch
              label="Возвращаться после смерти"
              checked={draft.chunkAnchor.onDeathReturn}
              disabled={anchorLocked}
              onCheckedChange={onDeathReturn => patchSection('chunkAnchor', { onDeathReturn })}
            />
          </div>
        </div>
        {draft.chunkAnchor.point === null ? (
          <p className="text-2xs text-ink-muted">
            Точка ещё не задана — она запишется при первом запуске с включённым якорем.
          </p>
        ) : (
          <p className="font-mono text-2xs text-ink-muted tabular-nums">
            Точка: {Math.round(draft.chunkAnchor.point.x)} / {Math.round(draft.chunkAnchor.point.y)}{' '}
            / {Math.round(draft.chunkAnchor.point.z)} · {draft.chunkAnchor.point.dimension}
          </p>
        )}
      </Block>

      <Block
        icon={<UtensilsCrossed aria-hidden className="size-4" />}
        title="Автоеда"
        hint="Бот сам ест, когда сытость падает ниже порога. Что есть нельзя — отмечается ниже; по умолчанию исключено то, что травит."
      >
        <Switch
          label="Включена"
          checked={draft.autoEat.enabled}
          disabled={!canOperate}
          onCheckedChange={enabled => patchSection('autoEat', { enabled })}
        />
        <NumberField
          label="Есть при сытости ниже"
          hint="Из 20. Полная шкала — 20, курица даёт 6."
          value={draft.autoEat.startAtFood}
          min={1}
          max={19}
          error={problems.startAtFood}
          disabled={autoEatLocked}
          className="sm:w-48"
          onChange={event => patchSection('autoEat', { startAtFood: Number(event.target.value) })}
        />

        <FieldGroup label="Не есть это" error={null}>
          {KNOWN_BANNED_FOODS.map(food => (
            <ToggleButton
              key={food.id}
              pressed={foods.known.includes(food.id)}
              disabled={autoEatLocked}
              onPressedChange={on =>
                patchSection('autoEat', prev => ({
                  bannedFoods: on
                    ? [...prev.bannedFoods, food.id]
                    : prev.bannedFoods.filter(item => normalizeFoodId(item) !== food.id)
                }))
              }
            >
              {food.label}
            </ToggleButton>
          ))}
        </FieldGroup>

        <ExtraFoods
          extra={foods.extra}
          disabled={autoEatLocked}
          onAdd={ids =>
            patchSection('autoEat', prev => ({ bannedFoods: [...prev.bannedFoods, ...ids] }))
          }
          onRemove={id =>
            patchSection('autoEat', prev => ({
              bannedFoods: prev.bannedFoods.filter(item => normalizeFoodId(item) !== id)
            }))
          }
        />
      </Block>

      <Block
        icon={<CalendarClock aria-hidden className="size-4" />}
        title="Расписание"
        hint="Вне окон расписания бот не запускается, а запущенный останавливается."
      >
        <Switch
          label="Включено"
          checked={draft.schedule.enabled}
          disabled={!canOperate}
          onCheckedChange={enabled => patchSection('schedule', { enabled })}
        />
        {problems.schedule === null ? null : (
          <p role="alert" className="text-2xs text-fail">
            {problems.schedule}
          </p>
        )}
        <Select
          label="Часовой пояс"
          value={draft.schedule.timezone}
          options={timezoneOptions(draft.schedule.timezone)}
          error={problems.timezone}
          disabled={scheduleLocked}
          className="sm:w-72"
          onValueChange={timezone => patchSection('schedule', { timezone })}
        />
        <ScheduleEditor
          windows={draft.schedule.windows}
          problems={problems.windows}
          disabled={scheduleLocked}
          onChange={windows => patchSection('schedule', { windows })}
        />
        {draft.schedule.windows.length > 0 ? (
          <p className="text-2xs text-ink-muted">
            Итого: {draft.schedule.windows.length}{' '}
            {plural(draft.schedule.windows.length, 'окно', 'окна', 'окон')} ·{' '}
            {daysLabel([...new Set(draft.schedule.windows.flatMap(window => window.days))])}
          </p>
        ) : null}
      </Block>

      <Block
        icon={<Radio aria-hidden className="size-4" />}
        title="Реакции на сервер"
        hint="«Увидел в чате такое-то — сделай то-то». Самое частое: за N минут до рестарта выйти и вернуться, чтобы не получить кик и не ждать полчаса."
      >
        <HookList
          hooks={draft.hooks}
          onChange={(hooks: ServerHook[]) => setDraft(current => ({ ...current, hooks }))}
        />
        {hookIssues.length === 0 ? null : (
          <p role="alert" className="text-2xs text-fail">
            Не сохранено — {hookIssues.join('; ')}
          </p>
        )}
      </Block>
    </Panel>
  )
}

/**
 * Обёртка для поля с множественным выбором: подпись группы, подсказка и ошибка
 * связаны с самой группой (`role="group"` + `aria-labelledby`), потому что
 * `<label for>` умеет ссылаться только на один контрол, а здесь их несколько.
 */
function FieldGroup({
  label,
  hint,
  error,
  children
}: {
  label: string
  hint?: string
  error: string | null
  children: JSX.Element | JSX.Element[]
}): JSX.Element {
  const labelId = useId()
  const hintId = `${labelId}-hint`
  const errorId = `${labelId}-error`
  const describedBy = [hint === undefined ? null : hintId, error === null ? null : errorId]
    .filter(item => item !== null)
    .join(' ')

  return (
    <div className="flex flex-col gap-1.5">
      <span id={labelId} className="text-xs font-medium text-ink-muted">
        {label}
      </span>
      <div
        role="group"
        aria-labelledby={labelId}
        aria-describedby={describedBy === '' ? undefined : describedBy}
        className="flex flex-wrap gap-1.5"
      >
        {children}
      </div>
      {hint === undefined ? null : (
        <p id={hintId} className="max-w-prose text-2xs text-ink-muted">
          {hint}
        </p>
      )}
      {error === null ? null : (
        <p id={errorId} role="alert" className="text-2xs text-fail">
          {error}
        </p>
      )}
    </div>
  )
}

/**
 * Предметы, которых нет в списке частых. Ввод подтверждается кнопкой или Enter,
 * а не «на каждую букву»: иначе набранное «Golden Apple» превращалось бы в
 * «golden_a» прямо под курсором — нормализация обязана случиться один раз.
 */
function ExtraFoods({
  extra,
  disabled,
  onAdd,
  onRemove
}: {
  extra: string[]
  disabled: boolean
  onAdd: (ids: string[]) => void
  onRemove: (id: string) => void
}): JSX.Element {
  const [text, setText] = useState('')
  const ids = parseFoodInput(text)

  const commit = (): void => {
    if (ids.length === 0) return
    onAdd(ids)
    setText('')
  }

  return (
    <div className="flex flex-col gap-2">
      {extra.length === 0 ? null : (
        <ul className="flex flex-wrap gap-1.5">
          {extra.map(id => (
            <li
              key={id}
              className="flex items-center gap-1 rounded-pill border border-line bg-panel-2 pr-1 pl-2.5 text-2xs text-ink-muted"
            >
              <span className="font-mono">{id}</span>
              <button
                type="button"
                aria-label={`Убрать ${id} из списка`}
                disabled={disabled}
                onClick={() => onRemove(id)}
                className="press flex size-5 items-center justify-center rounded-pill text-ink-faint hover:text-fail disabled:cursor-not-allowed disabled:opacity-45"
              >
                <X aria-hidden className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-start gap-2">
        <TextField
          label="Другой предмет"
          hint="Название как в игре, латиницей: golden_carrot. Несколько — через запятую."
          value={text}
          mono
          disabled={disabled}
          className="sm:w-72"
          onChange={event => setText(event.target.value)}
          onKeyDown={event => {
            if (event.key !== 'Enter') return
            event.preventDefault()
            commit()
          }}
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={disabled || ids.length === 0}
          onClick={commit}
          className="mt-6"
        >
          <Plus aria-hidden className="size-4" />
          Добавить
        </Button>
      </div>
    </div>
  )
}

function Block({
  icon,
  title,
  hint,
  children
}: {
  icon: JSX.Element
  title: string
  hint: string
  children: JSX.Element | (JSX.Element | null)[]
}): JSX.Element {
  return (
    <section className="flex flex-col gap-3 border-b border-line pb-5 last:border-0 last:pb-0">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-sm bg-panel-3 text-ink-muted">
          {icon}
        </span>
        <div className="min-w-0">
          <h3 className="font-display text-base font-semibold text-ink">{title}</h3>
          <p className="mt-0.5 max-w-prose text-2xs text-ink-muted">{hint}</p>
        </div>
      </div>
      <div className="flex flex-col gap-3 pl-9.5">{children}</div>
    </section>
  )
}

/** Русская форма числа: 1 окно, 2 окна, 5 окон. */
function plural(count: number, one: string, few: string, many: string): string {
  const mod100 = count % 100
  const mod10 = count % 10
  if (mod100 >= 11 && mod100 <= 14) return many
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return few
  return many
}
